import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Auth, authState, idToken, signInWithCustomToken, signOut, user } from '@angular/fire/auth';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '~env';
import { AuthUser } from '../core/auth-user.model';
import { AuthService } from '../core/auth.service';

@Injectable()
export class FirebaseAuthService implements AuthService, OnDestroy {
  private userState = new BehaviorSubject<AuthUser>(null);
  userState$ = this.userState.asObservable();

  private errors = new Subject<any>();
  errors$ = this.errors.asObservable();

  private loginChanged = new BehaviorSubject<boolean>(false);
  loginChanged$ = this.loginChanged.asObservable();

  private tokenExpiring = new Subject<number>();
  tokenExpiring$ = this.tokenExpiring.asObservable();

  constructor(
    private auth: Auth,
    private http: HttpClient,
  ) { }

  ngOnDestroy() {
    this.userState.complete();
    this.errors.complete();
    this.loginChanged.complete();
    this.tokenExpiring.complete();
  }

  signIn(): void {

    this.userState.next(null);
    this.errors.next(null);

    authState(this.auth)
      .pipe(
        take(1),
        switchMap(user =>
             this.acquireCustomToken()
              .pipe(
                mergeMap(customToken => 
                  signInWithCustomToken(this.auth, customToken)),
                map(userCreds => userCreds.user),
              ),
        ),
        map(user => {
          const authUser = new AuthUser();
          authUser.id = user.uid;
          authUser.email = user.email;
          authUser.name = user.displayName;
          authUser.roles = [];
          authUser.permitNumber = '';

          return authUser;
        }),
      )
      .subscribe(
        user => {
          this.loginChanged.next(true);
          this.userState.next(user);
        },
        err => {
          console.error('Firebase login error', err);
          this.loginChanged.next(false);
          this.errors.next(null);
        },
      );

  }

  // Not used for Firebase but must be implemented as it needs to be in AuthService implementations
  resetPassword(): void {
  }

  signOut(): void {
    signOut(this.auth).then(() => {
      console.log('Fire base Ath signout');
      this.userState.next(null);
      this.errors.next(null);
    });
  }

  isLoggedIn(): Observable<boolean> {
    const token$ = idToken(this.auth);
    const user$ = user(this.auth);

    return combineLatest([user$, token$]).pipe(map(data => {
      const [currentUser, token] = data;
      if (currentUser && token) {
        if (!!currentUser.uid.trim() && !!token.trim()) {
          this.loginChanged.next(true);
          return true;
        }
      }
      this.loginChanged.next(false);
      return false;
    }), catchError((err) => {
      console.error('firebase isLoggedIn error: ', err);
      return of(false);
    }));
  }

  private acquireCustomToken(): Observable<string> {
    return this.http
      .get<{ value: string }>(`${environment.functions.baseUrl}/firebase-custom-token`)
      .pipe(map(({ value }) => value), tap(token => 
        {
          if(!environment.production)
            console.log('firebse token!!!', token)
        }));
  }
}
